// React hook for the current auth state + user. Returns:
//   { session, user, displayName, email, avatarInitials, avatarUrl,
//     loading, isAdmin, adminChecked, signOut, refreshProfile }
//
// Components subscribe to this; on sign-in / sign-out the whole AppShell re-renders.
//
// `isAdmin` and `adminChecked` together let gated routes (the admin
// dashboard) wait for a real answer before deciding whether to redirect.
// Until `adminChecked` flips true the value of `isAdmin` is meaningless
// — the profiles.is_admin lookup is still in flight and the default
// `false` would otherwise bounce a legitimate admin to `/app` in a
// single frame.

import { useCallback, useEffect, useState } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "./supabase";
import { signOut as authSignOut } from "./auth";

export type AdminStatus = "pending" | "admin" | "not_admin";

export type CurrentUser = {
  session:    Session | null;
  user:       User | null;
  email:      string | null;
  displayName: string | null;
  avatarInitials: string;     // 1-2 chars
  /** Public Storage URL of the user's chosen profile picture (bucket
   *  `avatars`). Null while the profiles lookup is in flight OR when
   *  the user has never uploaded one — in which case renderers fall
   *  back to `avatarInitials` over the gradient. */
  avatarUrl:  string | null;
  loading:    boolean;
  /** Profiles.role lookup. 'pending' means the query is in flight (or
   *  no session yet); the resolved value is the row's role column.
   *  Use this directly in gates — there's no separate 'checked' flag,
   *  so the React 18 partial-render race (where one state flipped but
   *  the other hadn't) is impossible. */
  role:       "pending" | "family_admin" | "family_member" | "org_admin" | "admin";
  /** Back-compat boolean derived from role === 'admin'. */
  admin:      AdminStatus;
  signOut:    () => Promise<void>;
  /** Re-fetch the profile row (display_name, avatar_url, …). Used by
   *  the avatar uploader after a successful Storage write so the new
   *  photo appears immediately, and by anything else that mutates
   *  profiles. Cheap — the row is small — and safe to call multiple
   *  times. */
  refreshProfile: () => Promise<void>;
};
export function useCurrentUser(): CurrentUser {
  const [session,       setSession]       = useState<Session | null>(null);
  const [role,          setRole]          = useState<CurrentUser["role"]>("pending");
  const [admin,         setAdmin]         = useState<"pending" | "admin" | "not_admin">("pending");
  const [loading,       setLoading]       = useState<boolean>(true);
  const [avatarUrl,     setAvatarUrl]     = useState<string | null>(null);

  // Session + initial auth-state loading.
  useEffect(() => {
    let cancelled = false;
    supabase.auth.getSession().then(({ data }) => {
      if (cancelled) return;
      setSession(data.session);
      setLoading(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => {
      setSession(s);
      setLoading(false);
    });
    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, []);

  // Look up profiles.role + avatar_url for the current user. Existing
  // RLS in migration 014 lets a user read their own profile row. The
  // lookup flips role from 'pending' to a real value on completion
  // (success OR error) so auth gates can wait for a real answer before
  // deciding whether to redirect.
  //
  // avatar_url is read in the same SELECT so the avatar uploader only
  // needs to call refreshProfile() (instead of poking a separate
  // state) after a Storage write. Errors fetching the column fall
  // back to null — the gradient + initials avatar renders in that
  // case, which is the correct degraded UX.
  const refreshProfile = useCallback(async (): Promise<void> => {
    const uid = session?.user?.id;
    if (!uid) return;
    const { data, error } = await supabase
      .from("profiles")
      .select("role, avatar_url")
      .eq("id", uid)
      .maybeSingle();
    if (error) {
      setRole("family_member");
      setAdmin("not_admin");
      setAvatarUrl(null);
      return;
    }
    const r = (data?.role ?? "family_member") as CurrentUser["role"];
    setRole(r);
    setAdmin(r !== "admin" ? "not_admin" : "admin");
    setAvatarUrl(typeof data?.avatar_url === "string" ? data.avatar_url : null);
  }, [session?.user?.id]);

  useEffect(() => {
    const uid = session?.user?.id;
    if (!uid) {
      // No session yet: keep the role as 'pending' so the auth gate
      // waits for the session + lookup to resolve. The previous
      // version set 'not_admin' here, which caused useRequireAdmin
      // to immediately redirect an admin who was hard-reloading
      // /admin (the gate saw not_admin for a single frame before
      // the session loaded and the lookup re-fired, kicking the
      // admin back to /app before their profile could be
      // confirmed). 'pending' is the correct signal: the lookup
      // can't determine role without a session, so the gate must
      // wait.
      setRole("pending");
      setAdmin("pending");
      setAvatarUrl(null);
      return;
    }
    setRole("pending");
    setAdmin("pending");
    void refreshProfile();
  }, [session?.user?.id, refreshProfile]);

  const user = session?.user ?? null;
  const email = user?.email ?? null;
  // `user_metadata` is `Record<string, any>` from Supabase; narrow it
  // to the three display-name fields we actually read. Each field is
  // `unknown` after the guard; we only fall back if it's a non-empty
  // string, so a one-off check per field is enough.
  const pickMetaString = (k: "display_name" | "full_name" | "name"): string | null => {
    const m = user?.user_metadata;
    if (!m || typeof m !== "object") return null;
    const v = (m as Record<string, unknown>)[k];
    return typeof v === "string" && v.length > 0 ? v : null;
  };
  const displayName =
    pickMetaString("display_name") ||
    pickMetaString("full_name") ||
    pickMetaString("name") ||
    (email ? email.split("@")[0] : null) ||
    "你";

  const avatarInitials = (() => {
    const c = displayName.trim().charAt(0);
    return c || "你";
  })();

  return {
    session,
    user,
    email,
    displayName,
    avatarInitials,
    avatarUrl,
    admin,
    role,
    loading,
    signOut: async () => { await authSignOut(); },
    refreshProfile,
  };
}
